import { useFormik } from 'formik';
import IBAN from 'iban';
import { Fragment, useEffect } from 'react';
import { FaPercent } from 'react-icons/fa6';
import { PiEnvelopeSimple } from 'react-icons/pi';
import { isValidPhoneNumber } from "react-phone-number-input";
import { useNavigate, useParams } from 'react-router-dom';
import { Switch, Textarea } from 'rizzui';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import RequiredInput from '../../components/forms/InputHOC';
import RequiredPassword from '../../components/forms/PasswordHOC';
import PhoneInput from '../../components/forms/PhoneInputWrapper';
import SelectProvince from '../../components/forms/SelectProvince';
import useCorporations from '../../hooks/api-calls/useCorporations';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { Tutor } from '../../type/entities/tutor-type';
import { validateCifNif } from '../../utils/validatorFunctions';
import FormFooter from '../_layout/_footers/form-footer';
import TutorProductsForm from './components/TutorProductsForm';
import { mapProvidingCorporationsForForm } from './utils/providingCorporationNormalizer';

interface CreateFormProps {
  isLoading: boolean;
  submit: Function;
  data?: Tutor | undefined;
}

export const tutorType = [
  { value: 'internal', label: 'Interno (en nómina)' },
  { value: 'external', label: 'Externo (autónomo)' },
];

export const paymentMethods = [
  { value: 'hourly', label: 'Por horas' },
  { value: 'perStudent', label: 'Por alumno' },
];

const schema = yup.object({
  name: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  lastName: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  nifCif: yup.string().test('valid-cif-nif', 'El CIF/NIF no es válido', (value) => {
    if (value !== undefined && value.length > 0) {
      return validateCifNif(value);
    }
    return true;
  }).required('Campo obligatorio'),
  iban: yup.string().test('iban', 'El formato de IBAN no es válido', (value: any) => IBAN.isValid(value)),
  address: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  town: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  province: yup.string().min(1, 'Demasiado corto').max(100, 'Demasiado largo').required('Campo obligatorio'),
  postalCode: yup.string().min(5, 'Demasiado corto').max(5, 'Demasiado largo').matches(/^[0-9]+$/, 'Código postal inválido').required('Campo obligatorio'),
  tutorType: yup.string().required('Campo Obligatorio').oneOf(['internal', 'external'], 'Debes seleccionar entre Interno o Externo'),
  email: yup.string().email('Correo electrónico inválido').required('Campo obligatorio'),
  telephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
  telephoneSecondary: yup.string().nullable().test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value ?? '')),
  comments: yup.string().nullable(),
  withholding: yup.number().when('tutorType', {
    is: 'external',
    then: schema => schema.required('Campo obligatorio para tutor externo'),
    otherwise: schema => schema.notRequired().nullable(),
  })
    .min(0, 'El porcentaje no puede ser menor a 0%')
    .max(100, 'El porcentaje no puede ser mayor al 100%')
    .test(
      'max-2-decimals',
      'El número no puede tener más de 2 decimales',
      (value) => {
        if (value === undefined || value === null) return true;
        return /^\d+(\.\d{1,2})?$/.test(value.toString());
      }
    ),
  products: yup.array().of(yup.object().shape({
    id: yup.string().required('Campo obligatorio'),
    price: yup.number().when('tutorType', {
      is: 'external',
      then: schema => schema.required('Campo obligatorio para tutor externo'),
      otherwise: schema => schema.notRequired().nullable(),
    })
      .positive('Debe ser un número positivo')
      .test(
        'max-2-decimals',
        'El número no puede tener más de 2 decimales',
        (value) => {
          if (value === undefined || value === null) return true;
          return /^\d+(\.\d{1,2})?$/.test(value.toString());
        }
      ),
    paymentMethod: yup.string().nullable().when('tutorType', {
      is: 'external',
      then: schema => schema.required('Campo obligatorio para tutor externo'),
      otherwise: schema => schema.notRequired().nullable(),
    }),
  })).required('Campo obligatorio'),
  providingCorporations: yup.array().of(
    yup.object().shape({
      providingCorporationId: yup.string().required('Campo obligatorio'),
      platformUser: yup.string().required('Campo obligatorio'),
      platformPassword: yup.string().required('Campo obligatorio'),
      collaborationSignature: yup.boolean().required('Campo obligatorio'),
      availability: yup.array().of(
        yup.object().shape({
          dayOfWeek: yup.string().required(),
          slots: yup.array().of(
            yup.object().shape({
              startTime: yup.string().required(),
              endTime: yup.string().required(),
            })
          ).required(),
        })
      ).required('Campo obligatorio'),
    })
  ).min(1, 'Debes añadir al menos una empresa impartidora').required('Campo obligatorio'),
});

const TutorForm: React.FC<CreateFormProps> = ({ isLoading, submit, data }) => {

  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mode = data ? 'Editar' : 'Crear';
  const { fetchCorporations, getCorporationsList } = useCorporations({ active: true, corporation_function: 'providing' });

  useEffect(() => {
    fetchCorporations({});
  }, []);

  const formik = useFormik({
    initialValues: {
      tutorId: id,
      name: data?.name ?? '',
      lastName: data?.lastName ?? '',
      nifCif: data?.nifCif ?? '',
      iban: data?.iban ?? '',
      address: data?.address ?? '',
      town: data?.town ?? '',
      province: data?.province ?? '',
      postalCode: data?.postalCode ?? '',
      tutorType: data?.tutorType ?? '',
      email: data?.email ?? '',
      telephone: data?.telephone ?? '',
      telephoneSecondary: data?.telephoneSecondary ?? '',
      comments: data?.comments ?? '',
      products: data?.products
        ? data.products.map((p: any) => ({
            tutorHasProductId: p.id,
            id: p.product.id,
            price: p.pricePerStudent,
            paymentMethod: p.paymentMethod,
            name: `${p.product.name}`,
          }))
        : [],
      withholding: data?.withholding ?? 0,
      providingCorporations: mapProvidingCorporationsForForm(data),
      password: '',
      confirmPassword: '',
      active: data?.active ?? '',
    },
    validationSchema: schema,
    validateOnBlur: false,
    // validateOnChange: false,
    onSubmit: async (values) => {
      submit(values);
    }
  });

  const allProvidingCorporationsOptions = getCorporationsList();
  const selectedProvidingCorporationIds = ((formik.values as any)?.providingCorporations ?? [])
    .map((pc: any) => pc?.providingCorporationId)
    .filter((id: any) => !!id);

  const hasAssignedProducts = Array.isArray((formik.values as any)?.products) && (formik.values as any).products.length > 0;
  const canEditTutorType = !(mode === 'Editar' && hasAssignedProducts);

  const hasAllProvidingCorporationsAssigned =
    allProvidingCorporationsOptions.length > 0 &&
    new Set(selectedProvidingCorporationIds).size >= allProvidingCorporationsOptions.length;

  const getProvidingOptionsForRow = (idx: number) => {
    const providingCorporations = (formik.values as any)?.providingCorporations ?? [];
    const currentSelected = providingCorporations[idx]?.providingCorporationId;
    const selectedInOtherRows = providingCorporations
      .map((pc: any, rowIdx: number) => rowIdx !== idx ? pc?.providingCorporationId : null)
      .filter((id: any) => !!id);

    return allProvidingCorporationsOptions.filter((option: any) =>
      option.value === currentSelected || !selectedInOtherRows.includes(option.value)
    );
  };

  const getContent = () => {
    return (
      <>
        <div className="@container">
          <form onSubmit={formik.handleSubmit}>
            <FormGroup
              title="Información General"
              description="Datos principales del tutor"
              className='pt-3 pb-4'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-5"
            >
              <RequiredInput
                id='name'
                label="Nombre"
                formik={formik}
              />

              <RequiredInput
                id='lastName'
                label="Apellidos"
                formik={formik}
              />

              <RequiredInput
                id='nifCif'
                label="CIF/NIF"
                formik={formik}
              />

              <RequiredInput
                id='email'
                type="email"
                label="Correo Electrónico"
                className="md:col-span-2"
                prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                formik={formik}
              />

              <RequiredInput
                id='iban'
                label="IBAN"
                formik={formik}
              />

              <PhoneInput
                id='telephone'
                label='Teléfono'
                onChange={e => { formik.setFieldValue('telephone', e) }}
                value={formik.values.telephone}
                error={formik.errors.telephone}
              />

              <PhoneInput
                id='telephoneSecondary'
                label='Teléfono Secundario'
                onChange={e => { formik.setFieldValue('telephoneSecondary', e) }}
                value={formik.values.telephoneSecondary}
                error={formik.errors.telephoneSecondary}
              />

              <CustomSelect
                id='tutorType'
                required
                label="Tipo de Tutor"
                options={tutorType}
                isDisabled={!canEditTutorType}
                onChange={(e: any) => {
                  if (!canEditTutorType) return;
                  formik.setFieldValue('tutorType', e ? e.value : null);
                  formik.setFieldValue('products', []); // Reset products when changing tutor type to avoid validation errors
                  formik.setFieldValue('withholding', null); // Reset withholding when changing tutor type
                }}
                value={{ value: formik.values.tutorType, label: tutorType.find((g: any) => g.value === formik.values.tutorType)?.label }}
                error={formik.errors.tutorType}
              />
              {!canEditTutorType && (
                <div className="md:col-span-2 text-xs text-gray-500 -mt-2">
                  No puedes cambiar el tipo de tutor mientras tenga productos asignados.
                </div>
              )}

              <CustomSelect
                id='withholding'
                required={formik.values.tutorType === 'external'}
                label="Retenciones"
                options={[]}
                onChange={() => {}}
                value={null as any}
                error={undefined as any}
                className="hidden"
              />

              {formik.values.tutorType === 'external' && (
                <RequiredInput
                  id='withholding'
                  type="number"
                  label="Retenciones"
                  formik={formik}
                  suffix={<FaPercent />}
                />
              )}

              <RequiredPassword
                id='password'
                label="Contraseña"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                onChange={formik.handleChange}
                value={formik.values.password}
                error={formik.errors.password}
              />

              <RequiredPassword
                id='confirmPassword'
                label="Confirmar contraseña"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                onChange={formik.handleChange}
                value={formik.values.confirmPassword}
                error={formik.errors.confirmPassword}
              />
            </FormGroup>

            <hr className="m-4"/>

            <TutorProductsForm
             formik={formik}
             className='pt-6 pb-4'
            />

            <hr className="m-4" />

            <FormGroup
              title="Empresas impartidoras"
              description="Asignación de impartidoras, credenciales y disponibilidad por tramos"
              className='pt-6 pb-4'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-1"
            >
              <div className="space-y-4">
                {(formik.values as any).providingCorporations?.map((pc: any, idx: number) => (
                  <div key={idx} className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div className="grid md:grid-cols-4 gap-4">
                      <CustomSelect
                        id={`providingCorporations[${idx}].providingCorporationId`}
                        required
                        label="Empresa impartidora"
                        options={getProvidingOptionsForRow(idx)}
                        value={
                          pc.providingCorporationId
                            ? allProvidingCorporationsOptions.find((c: any) => c.value === pc.providingCorporationId) || null
                            : null
                        }
                        onChange={(value: any) => formik.setFieldValue(`providingCorporations[${idx}].providingCorporationId`, value?.value || '')}
                        placeholder="Selecciona impartidora"
                        isSearchable
                        isClearable={false}
                        error={(formik.errors as any)?.providingCorporations?.[idx]?.providingCorporationId}
                      />

                      <RequiredInput
                        id={`providingCorporations[${idx}].platformUser`}
                        label="Usuario de la plataforma"
                        value={pc.platformUser}
                        onChange={(e: any) => formik.setFieldValue(`providingCorporations[${idx}].platformUser`, e.target.value)}
                        error={(formik.errors as any)?.providingCorporations?.[idx]?.platformUser}
                        formik={formik as any}
                      />

                      <RequiredPassword
                        id={`providingCorporations[${idx}].platformPassword`}
                        label="Contraseña de la plataforma"
                        className="[&>label>span]:font-medium"
                        inputClassName="text-sm"
                        onChange={formik.handleChange}
                        value={pc.platformPassword}
                        error={(formik.errors as any)?.providingCorporations?.[idx]?.platformPassword}
                      />

                      <div className="flex items-end pb-1">
                        <Switch
                          id={`providingCorporations[${idx}].collaborationSignature`}
                          label="Requiere firma de colaboración"
                          switchKnobClassName='bg-secondary'
                          checked={!!pc.collaborationSignature}
                          onChange={(e: any) => formik.setFieldValue(`providingCorporations[${idx}].collaborationSignature`, e?.target ? e.target.checked : !!e)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium">Disponibilidad</div>
                      <div className="grid md:grid-cols-2 gap-3">
                        {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map((day) => {
                          const dayIdx = (pc.availability || []).findIndex((d: any) => d.dayOfWeek === day);
                          const dayObj = dayIdx >= 0 ? pc.availability[dayIdx] : null;
                          const labelMap: any = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' };

                          return (
                            <div key={day} className="rounded-md border border-gray-100 p-3">
                              <div className="flex items-center justify-between">
                                <div className="font-medium text-sm">{labelMap[day]}</div>
                                <Switch
                                  label=""
                                  switchKnobClassName='bg-secondary'
                                  checked={dayIdx >= 0}
                                  onChange={() => {
                                    if (dayIdx >= 0) {
                                      const next = [...pc.availability];
                                      next.splice(dayIdx, 1);
                                      formik.setFieldValue(`providingCorporations[${idx}].availability`, next);
                                    } else {
                                      formik.setFieldValue(`providingCorporations[${idx}].availability`, [...(pc.availability || []), { dayOfWeek: day, slots: [] }]);
                                    }
                                  }}
                                />
                              </div>

                              {dayIdx >= 0 && (
                                <div className="mt-3 space-y-2">
                                  {(dayObj?.slots || []).map((slot: any, sIdx: number) => (
                                    <div key={sIdx} className="flex gap-2 items-center">
                                      <input
                                        type="time"
                                        className="border rounded px-2 py-1 text-sm w-32"
                                        value={slot.startTime || ''}
                                        onChange={(e) => formik.setFieldValue(`providingCorporations[${idx}].availability[${dayIdx}].slots[${sIdx}].startTime`, e.target.value)}
                                      />
                                      <span className="text-sm text-gray-500">-</span>
                                      <input
                                        type="time"
                                        className="border rounded px-2 py-1 text-sm w-32"
                                        value={slot.endTime || ''}
                                        onChange={(e) => formik.setFieldValue(`providingCorporations[${idx}].availability[${dayIdx}].slots[${sIdx}].endTime`, e.target.value)}
                                      />
                                      <button
                                        type="button"
                                        className="text-sm text-red-600"
                                        onClick={() => {
                                          const nextSlots = [...(dayObj?.slots || [])];
                                          nextSlots.splice(sIdx, 1);
                                          formik.setFieldValue(`providingCorporations[${idx}].availability[${dayIdx}].slots`, nextSlots);
                                        }}
                                      >
                                        Eliminar
                                      </button>
                                    </div>
                                  ))}

                                  <button
                                    type="button"
                                    className="text-sm text-primary"
                                    onClick={() => {
                                      const nextSlots = [...(dayObj?.slots || []), { startTime: '09:00', endTime: '14:00' }];
                                      formik.setFieldValue(`providingCorporations[${idx}].availability[${dayIdx}].slots`, nextSlots);
                                    }}
                                  >
                                    Añadir tramo
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {(formik.errors as any)?.providingCorporations?.[idx]?.availability && (
                        <div className="text-sm text-red-600">{(formik.errors as any).providingCorporations[idx].availability}</div>
                      )}
                    </div>

                    {(formik.values as any).providingCorporations.length > 1 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="text-sm text-red-600"
                          onClick={() => {
                            const next = [...(formik.values as any).providingCorporations];
                            next.splice(idx, 1);
                            formik.setFieldValue('providingCorporations', next);
                          }}
                        >
                          Quitar impartidora
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                <div>
                  <button
                    type="button"
                    className="text-sm text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={hasAllProvidingCorporationsAssigned}
                    onClick={() => {
                      if (hasAllProvidingCorporationsAssigned) return;
                      const next = [...(formik.values as any).providingCorporations, { tutorProvidingCorporationId: '', providingCorporationId: '', platformUser: '', platformPassword: '', collaborationSignature: false, availability: [] }];
                      formik.setFieldValue('providingCorporations', next);
                    }}
                  >
                    {hasAllProvidingCorporationsAssigned ? 'Todas las impartidoras asignadas' : 'Añadir impartidora'}
                  </button>
                  {hasAllProvidingCorporationsAssigned && (
                    <div className="text-xs text-gray-500 mt-1">
                      Este tutor ya tiene asignadas todas las empresas impartidoras disponibles.
                    </div>
                  )}
                </div>
              </div>
            </FormGroup>

            <FormGroup
              title="Información Adicional"
              description="Datos adicionales del tutor"
              className='pt-6 pb-4'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-5"
            >
              <RequiredInput
                id='address'
                label="Dirección"
                className="md:col-span-2"
                formik={formik}
              />

              <RequiredInput
                id='town'
                label="Población"
                formik={formik}
              />

              <SelectProvince
                required
                id='province'
                formik={formik}
              />

              <RequiredInput
                id='postalCode'
                label="Código Postal"
                formik={formik}
              />

              <Textarea
                id='comments'
                label='Comentarios'
                rows={3}
                className="[&>label>span]:font-medium md:col-span-4"
                onChange={formik.handleChange}
                value={formik.values.comments}
                error={formik.errors.comments}
              />

            </FormGroup>
          </form>
        </div>
      </>
    )
  };

  return (
    <Fragment>
      <Page container="fluid">
        {(mode === "Editar" && data) && getContent()}
        {mode === "Crear" && getContent()}
      </Page>
      <FormFooter
        submitBtnText={mode + ' Tutor'}
        handleCancelBtn={() => navigate(-1)}
        handleSubmitBtn={formik.submitForm} isLoading={isLoading}
      />
    </Fragment>
  )
};

export default TutorForm;
